import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseService } from './base.service';
import {
  Company,
  CreateCompanyDto,
  UpdateCompanyDto,
  UpdateCompanySubscriptionDto,
  CompanyStats,
  ApiResponse,
  CompanyDetailsResponse,
  CompanyBilling,
  BulkStatusUpdate,
  ExportOptions,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class CompaniesService extends BaseService<Company> {
  protected endpoint = 'companies';

  /**
   * Get company details with stats and recent activity
   */
  getCompanyDetails(id: string): Observable<CompanyDetailsResponse> {
    return this.http.get<CompanyDetailsResponse>(`${this.baseUrl}/${this.endpoint}/${id}/details`);
  }

  /**
   * Get company statistics
   */
  getCompanyStats(id: string): Observable<CompanyStats> {
    return this.http.get<CompanyStats>(`${this.baseUrl}/${this.endpoint}/${id}/stats`);
  }

  /**
   * Activate company
   */
  activate(id: string): Observable<ApiResponse<Company>> {
    return this.http.patch<ApiResponse<Company>>(
      `${this.baseUrl}/${this.endpoint}/${id}/activate`,
      {},
    );
  }

  /**
   * Deactivate company
   */
  deactivate(id: string): Observable<ApiResponse<Company>> {
    return this.http.patch<ApiResponse<Company>>(
      `${this.baseUrl}/${this.endpoint}/${id}/deactivate`,
      {},
    );
  }

  /**
   * Restore deleted company
   * POST /companies/:id/restore
   */
  restore(id: string): Observable<ApiResponse<Company>> {
    return this.http.post<ApiResponse<Company>>(
      `${this.baseUrl}/${this.endpoint}/${id}/restore`,
      {},
    );
  }

  /**
   * Update company status
   */
  updateStatus(id: string, status: 'active' | 'inactive'): Observable<ApiResponse<Company>> {
    return this.http.patch<ApiResponse<Company>>(`${this.baseUrl}/${this.endpoint}/${id}/status`, {
      status,
    });
  }

  /**
   * Bulk update company status
   */
  bulkUpdateStatus(
    data: BulkStatusUpdate,
  ): Observable<ApiResponse<{ updated: number; companies: Company[] }>> {
    return this.http.patch<ApiResponse<{ updated: number; companies: Company[] }>>(
      `${this.baseUrl}/${this.endpoint}/bulk/status`,
      data,
    );
  }

  /**
   * Upload company logo
   */
  uploadLogo(id: string, photo_base64: string): Observable<ApiResponse<{ logoUrl: string }>> {


    return this.http.post<ApiResponse<{ logoUrl: string }>>(
      `${this.baseUrl}/${this.endpoint}/${id}/logo`,
      {photo_base64},
    );
  }

  /**
   * Delete company logo
   */
  deleteLogo(id: string): Observable<ApiResponse<{ message: string }>> {
    return this.http.delete<ApiResponse<{ message: string }>>(
      `${this.baseUrl}/${this.endpoint}/${id}/logo`,
    );
  }

  /**
   * Get company billing information
   */
  getBillingInfo(id: string): Observable<CompanyBilling> {
    return this.http.get<CompanyBilling>(`${this.baseUrl}/${this.endpoint}/${id}/billing`);
  }

  /**
   * Update company billing information
   */
  updateBillingInfo(
    id: string,
    data: Partial<CompanyBilling>,
  ): Observable<ApiResponse<CompanyBilling>> {
    return this.http.patch<ApiResponse<CompanyBilling>>(
      `${this.baseUrl}/${this.endpoint}/${id}/billing`,
      data,
    );
  }

  /**
   * Update company subscription information
   */
  updateSubscription(
    id: string,
    data: UpdateCompanySubscriptionDto,
  ): Observable<unknown> {
    return this.http.patch<unknown>(
      `${this.baseUrl}/${this.endpoint}/${id}/subscription`,
      data,
    );
  }

  /**
   * Export companies data
   */
  exportCompanies(options: ExportOptions): Observable<Blob> {
    let params = new HttpParams();
    params = params.set('format', options.format);
    if (options.include_stats !== undefined) {
      params = params.set('include_stats', options.include_stats.toString());
    }

    return this.http.get(`${this.baseUrl}/${this.endpoint}/export`, {
      params,
      responseType: 'blob',
    });
  }

  /**
   * Search companies
   */
  searchCompanies(
    query: string,
    filters?: {
      status?: 'active' | 'inactive';
      subscription_plan?: string;
      province?: string;
    },
  ): Observable<ApiResponse<Company[]>> {
    let params = new HttpParams();
    params = params.set('q', query);

    if (filters) {
      if (filters.status) params = params.set('status', filters.status);
      if (filters.subscription_plan)
        params = params.set('subscription_plan', filters.subscription_plan);
      if (filters.province) params = params.set('province', filters.province);
    }

    return this.http.get<ApiResponse<Company[]>>(`${this.baseUrl}/${this.endpoint}/search`, {
      params,
    });
  }
}
